/**
 * Shared filter vocabulary for the bulk conversation viewer
 * (`/employee/conversations`). One source of truth for the date fields you can
 * range-filter on, imported by the LinkRouter builder, the viewer page, and the
 * `getConversationList` tRPC query so param names never drift.
 *
 * Filters supported by the viewer:
 *  - lead stage        (IN — a set of LeadStage)
 *  - campaign stage    (IN — a set of CampaignStage)
 *  - response stage    (IN — a set of ResponseStage)
 *  - date ranges       (from/to on each field below)
 *
 * `=` is expressed as a one-element IN (enums) or a from==to day (dates), so
 * this set covers equals / in / range without extra operators.
 */

// Date fields you can filter with a from/to range. "Stage-entry" fields are
// derived from the lead's context.history transitions; column fields are direct.
export const CONVERSATION_DATE_FIELDS = [
  'created',
  'updated',
  'queued',
  'requestSent',
  'connected',
  'replied',
  'booked',
] as const;

export type ConversationDateField = (typeof CONVERSATION_DATE_FIELDS)[number];

export const CONVERSATION_DATE_FIELD_LABELS: Record<
  ConversationDateField,
  string
> = {
  created: 'Lead created',
  updated: 'Last updated',
  queued: 'Queued at',
  requestSent: 'Request sent at',
  connected: 'Connected at',
  replied: 'Replied at',
  booked: 'Call booked at',
};

/** URL param name for the lower bound of a date field (e.g. "queuedFrom"). */
export function dateFromParam(field: ConversationDateField): string {
  return `${field}From`;
}

/** URL param name for the upper bound of a date field (e.g. "queuedTo"). */
export function dateToParam(field: ConversationDateField): string {
  return `${field}To`;
}

/** A resolved from/to window for one date field (ISO strings, either optional). */
export type ConversationDateRange = {
  field: ConversationDateField;
  from?: string;
  to?: string;
};
